import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Button,
  ButtonGroup,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Grow,
  ClickAwayListener,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

const searchModes = ['Customers', 'Documents'];

interface SearchModeToggleProps {
  className?: string;
}

export const SearchModeToggle: React.FC<SearchModeToggleProps> = ({ className }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(() => {
    // Determine initial selection based on current route
    if (location.pathname.startsWith('/documents')) {
      return 1; // Documents
    }
    return 0; // Customers
  });

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    index: number
  ) => {
    setSelectedIndex(index);
    setOpen(false);

    // Navigate based on selection
    if (index === 0) {
      // Customers - navigate to search page
      navigate('/search');
    } else {
      // Documents - navigate to documents dashboard
      navigate('/documents');
    }
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  };

  // Update selected index when route changes
  useEffect(() => {
    if (location.pathname.startsWith('/documents')) {
      setSelectedIndex(1);
    } else if (location.pathname === '/search') {
      setSelectedIndex(0);
    }
  }, [location.pathname]);

  return (
    <>
      <ButtonGroup
        variant="contained"
        ref={anchorRef}
        aria-label="search mode selector"
        sx={{
          minWidth: 'fit-content',
          boxShadow: 'none',
        }}
        className={className}
      >
        <Button
          size="small"
          aria-controls={open ? 'search-mode-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="menu"
          onClick={handleToggle}
          sx={{
            height: 'fit-content',
            minHeight: 36,
            backgroundColor: '#0B93DF',
            boxShadow: 'none',
            textTransform: 'capitalize',
            fontSize: '0.875rem',
            px: 1.5,
          }}
          data-testid="header-search-mode-toggle"
        >
          {searchModes[selectedIndex]}
          <ArrowDropDownIcon sx={{ ml: 0.5, fontSize: '1rem' }} />
        </Button>
      </ButtonGroup>
      <Popper
        sx={{
          zIndex: 1300,
        }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="search-mode-menu" autoFocusItem>
                  {searchModes.map((mode, index) => (
                    <MenuItem
                      key={mode}
                      selected={index === selectedIndex}
                      onClick={(event) => handleMenuItemClick(event, index)}
                      sx={{ py: '0.75rem' }}
                    >
                      {mode}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};
